import { careModelAnalytics } from 'src/services/analytics';
import { ref } from 'vue';

import { createAuditRun } from '../../application/useCases/createAuditRun';
import { TASK_STATUSES } from '../../domain/constants/auditResults';
import { auditSessionActivity } from '../../infrastructure/api/auditApi';

/**
 * Composable that manages the state and logic for running audits on notes.
 * It provides a simple interface for preparing and executing audits, as well as tracking their progress.
 * @returns {{
 *  auditTasks: import('vue').Ref<import('../entities/AuditTask.types').AuditTask[]>,
 *  isRunning: import('vue').Ref<boolean>,
 *  prepare: (sessionActivities: Object[]) => void,
 *  run: (settings: { focusAreas: string[], strictness: string }) => Promise<void>,
 *  cancel: () => void,
 * }}
 */
// eslint-disable-next-line import/prefer-default-export
export function useAuditRunner() {
  const auditTasks = ref([]);
  const isRunning = ref(false);

  // Wire the use case to the concrete infrastructure implementation
  const runner = createAuditRun(auditSessionActivity);

  /**
   * Prepare the audit tasks based on the provided session activities.
   * @param {Object[]} sessionActivities - The session activities to audit
   * @returns {void}
   */
  function prepare(sessionActivities) {
    auditTasks.value = runner.prepare(sessionActivities);
  }

  /**
   * Run the audit process: start multiple workers to process tasks concurrently.
   * Prevents multiple simultaneous runs and ensures all tasks are processed before finishing.
   * @param {AuditSettings} settings - Audit settings to pass to each task
   * @returns {Promise<void>}
   */
  async function run(settings) {
    if (isRunning.value) {
      return;
    }
    isRunning.value = true;
    try {
      await runner.run(
        auditTasks.value,
        settings,
        (task) => {
          careModelAnalytics.trackAuditActionInitiated(
            'bulk',
            task.sessionActivity.id,
            settings.focusAreas,
            settings.strictness,
            task.taskStatus === TASK_STATUSES.COMPLETED,
            task.verdict ?? null,
          );
        },
      );
    } finally {
      isRunning.value = false;
    }
  }

  /**
   * Cancel the ongoing audit process by aborting the workers. This will cause in-flight
   * API calls to fail and mark remaining tasks as canceled.
   * @returns {void}
   */
  function cancel() {
    runner.cancel();
  }

  return { auditTasks, isRunning, prepare, run, cancel };
}
